/** SIP users (V1-E03-T01, TEL-9): the phones and browsers that register to the edge and call in. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";
import { ApiError, data, type Schemas } from "../api/client";
import { Page, Section, useApiQuery } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState } from "../ui/states";
import { Table } from "../ui/Table";

type SipUser = Schemas["SipUserOut"];
const USERNAME = /^[a-z0-9][a-z0-9._-]{2,63}$/;
const input = "rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";

export function SipUsersPage() {
  const t = useT();
  const canWrite = useCan("telephony:write");
  const users = useApiQuery("sip-users", (api) => api.GET("/v1/sip-users", { params: { query: { limit: 100 } } }));
  return (
    <Page title={t("nav.sipUsers")} description={t("sipUsers.description")}>
      {canWrite && <NewSipUser />}
      <Section query={users}>
        {(page) =>
          page.data.length === 0 ? (
            <Empty title={t("sipUsers.empty")}>
              <p>{t("sipUsers.emptyBody")}</p>
            </Empty>
          ) : (
            <Table
              caption={t("nav.sipUsers")}
              rows={page.data}
              rowKey={(u) => u.id}
              columns={[
                { header: t("sipUsers.col.username"), cell: (u) => <span className="font-mono">{u.username}</span> },
                { header: t("sipUsers.col.name"), cell: (u) => u.display_name },
                { header: t("sipUsers.col.enabled"), cell: (u) => t(u.enabled ? "common.yes" : "common.no") },
                ...(canWrite ? [{ header: "", cell: (u: SipUser) => <Delete user={u} /> }] : []),
              ]}
            />
          )
        }
      </Section>
    </Page>
  );
}

/** A new user: its username (unique installation-wide), a password (kept only as its digest), a name. */
function NewSipUser() {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const ids = { username: useId(), password: useId(), name: useId(), operator: useId() };
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [operator, setOperator] = useState(false);
  const create = useMutation({
    mutationFn: () => data(api.POST("/v1/sip-users", { body: { username, password, display_name: name, enabled: true, operator,
                                                            wrap_up_s: 0 } })),
    onSuccess: async () => {
      setUsername("");
      setPassword("");
      setName("");
      setOperator(false);
      await queries.invalidateQueries({ queryKey: ["sip-users", tenant] });
    },
  });
  const usernameProblem = username && !USERNAME.test(username) ? t("sipUsers.usernameRule") : null;
  const passwordProblem = password && password.length < 12 ? t("sipUsers.passwordRule") : null;
  const taken = create.error instanceof ApiError && create.error.status === 409;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (username && password && !usernameProblem && !passwordProblem) create.mutate();
  };
  return (
    <form onSubmit={submit} className="mb-6 flex flex-wrap items-end gap-3" aria-label={t("sipUsers.new")}>
      <label htmlFor={ids.username} className="flex flex-col gap-1 text-sm">
        {t("sipUsers.col.username")}
        <input id={ids.username} className={input} value={username} autoComplete="off"
          onChange={(e) => setUsername(e.target.value.toLowerCase())} aria-invalid={Boolean(usernameProblem)} />
      </label>
      <label htmlFor={ids.password} className="flex flex-col gap-1 text-sm">
        {t("sipUsers.password")}
        <input id={ids.password} className={input} type="password" value={password} autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)} aria-invalid={Boolean(passwordProblem)} />
      </label>
      <label htmlFor={ids.name} className="flex flex-col gap-1 text-sm">
        {t("sipUsers.col.name")}
        <input id={ids.name} className={input} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
      </label>
      <label htmlFor={ids.operator} className="flex items-center gap-2 text-sm">
        <input id={ids.operator} type="checkbox" checked={operator} onChange={(e) => setOperator(e.target.checked)} />
        {t("sipUsers.operator")}
      </label>
      <Button type="submit" disabled={create.isPending || !username || !password}>{t("sipUsers.add")}</Button>
      {(usernameProblem ?? passwordProblem) && <p className="w-full text-sm text-[var(--color-danger)]">{usernameProblem ?? passwordProblem}</p>}
      {taken ? <p className="w-full text-sm text-[var(--color-danger)]">{t("sipUsers.taken")}</p>
        : create.isError && <div className="w-full"><ErrorState error={create.error} /></div>}
    </form>
  );
}

function Delete({ user }: { user: SipUser }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const remove = useMutation({
    mutationFn: () => data(api.DELETE("/v1/sip-users/{user_id}", { params: { path: { user_id: user.id } } })),
    onSuccess: () => queries.invalidateQueries({ queryKey: ["sip-users", tenant] }),
  });
  return (
    <Button variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate()}
      aria-label={t("sipUsers.deleteFor", { username: user.username })}>
      {t("sipUsers.delete")}
    </Button>
  );
}
